// Static Lucide icon library (FR 2026-05-31 — Tool invocation framework).
//
// Tool manifests reference Lucide icons by string name (e.g. "Gauge",
// "ScanLine"). The web app needs the actual component to render. Two
// options were on the table:
//
//   - static map (this file)        — explicit, tree-shakable, bundle
//                                     stays the same size as today.
//   - dynamic `lucide-react/dynamic` — every manifest can use any of
//                                     the ~1.5k icons, but the entire
//                                     library ends up in the bundle.
//
// We picked static (FR §9 decision). Adding a new icon to a manifest
// means adding the name to this map first. The runtime mapper falls
// back to a generic `Square` icon for unknown names so an unfamiliar
// manifest doesn't crash the palette.
//
// To add: import the component below + add an entry to ICON_BY_NAME.
// Keep the list alphabetized inside each grouping.

import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  BookOpen,
  ExternalLink,
  FileBarChart,
  FileText,
  Gauge,
  Globe,
  Link2,
  Network,
  Scissors,
  ScanLine,
  Sparkles,
  Square,
  Workflow,
} from 'lucide-react';

/** Mapping of manifest-name → Lucide component. Names are
 *  case-sensitive and match Lucide's PascalCase export names. */
export const ICON_BY_NAME: Record<string, LucideIcon> = {
  AlertCircle,
  BookOpen,
  ExternalLink,
  FileBarChart,
  FileText,
  Gauge,
  Globe,
  Link2,
  Network,
  ScanLine,
  Scissors,
  Sparkles,
  Workflow,
};

/** Resolve a manifest icon name to a Lucide component. Falls back to
 *  `Square` for unknown names so an unfamiliar manifest renders a
 *  visible placeholder instead of crashing the palette. */
export function resolveIcon(name: string | undefined): LucideIcon {
  if (!name) return Square;
  return ICON_BY_NAME[name] ?? Square;
}
